// ─── Train Animation Hook ────────────────────────────────────────────────────
// Wires TrainEngine to TripContext trip legs. Self-contained — MapView
// doesn't need to know about it at all.
//
// Usage (in App.tsx):
//   const mapRef = useRef<L.Map | null>(null);
//   const { trainEnabled, setTrainEnabled } = useTrainAnimation(mapRef);

import { useEffect, useState, useCallback, useRef, type MutableRefObject } from "react";
import { TrainEngine, isTrainEnabled, setTrainEnabled as persistEnabled } from "../components/AnimatedTrain";
import { useTrip } from "../contexts/TripContext";

export function useTrainAnimation(mapRef: MutableRefObject<L.Map | null>) {
  const { mapTripLegs: tripLegs, tripStops } = useTrip();

  // Local state mirrors localStorage so the toggle is reactive
  const [trainEnabledState, setTrainEnabledState] = useState(isTrainEnabled);
  const engineRef = useRef<TrainEngine | null>(null);

  // Track when map becomes available (ref mutation doesn't trigger re-render)
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => {
    // Check periodically until mapRef is set, then stop
    if (mapRef.current) { setMapReady(true); return; }
    const iv = setInterval(() => {
      if (mapRef.current) { setMapReady(true); clearInterval(iv); }
    }, 100);
    return () => clearInterval(iv);
  }, [mapRef]);

  // Setter that persists + updates React state
  const setTrainEnabled = useCallback((on: boolean) => {
    persistEnabled(on);
    setTrainEnabledState(on);
  }, []);

  // ── Build coords + create engine atomically (single effect) ───────
  // Previously split across two effects, causing a stale-ref race: the
  // engine lifecycle effect would read coordsRef before the coord-building
  // effect had updated it on the same render cycle.

  useEffect(() => {
    const map = mapRef.current;

    // Always destroy previous engine first
    engineRef.current?.destroy();
    engineRef.current = null;

    if (!map || !trainEnabledState) return;

    // Build concatenated coords from trip legs
    const allCoords: [number, number][] = [];
    for (let i = 0; i < tripStops.length - 1; i++) {
      const leg = tripLegs.find(l => l.fromStationId === tripStops[i].id && l.toStationId === tripStops[i + 1].id);
      if (!leg?.routeCoords?.length || leg.ghostLoading || leg.ghostError || leg.ghostThrottled) break;
      // Stitch: skip the first point of subsequent legs if it matches the
      // last point of the previous segment (avoid duplicate waypoints).
      const previousPoint = allCoords.at(-1);
      const start =
        leg.routeCoords[0][0] === previousPoint?.[0] &&
        leg.routeCoords[0][1] === previousPoint[1]
          ? 1
          : 0;
      for (let i = start; i < leg.routeCoords.length; i++) {
        allCoords.push(leg.routeCoords[i]);
      }
    }

    if (allCoords.length < 2) return;

    const engine = new TrainEngine(map);
    engine.setCoords(allCoords);
    engineRef.current = engine;

    return () => {
      engine.destroy();
      if (engineRef.current === engine) engineRef.current = null;
    };
  }, [mapRef, trainEnabledState, tripLegs, tripStops, mapReady]);

  return { trainEnabled: trainEnabledState, setTrainEnabled };
}
